import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle, Bot, Plus, CheckCircle2, Clock, Scale, MessageSquare } from 'lucide-react';

export default function DisputesPage() {
  const { api, user } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [generatingTimeline, setGeneratingTimeline] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [form, setForm] = useState({ project_id: '', task_id: '', against_user: '', type: 'payment', description: '' });
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/disputes'),
      api.get('/projects'),
      api.get('/users').catch(() => ({ data: [] })),
    ]).then(([d, p, u]) => { setDisputes(d.data); setProjects(p.data); setUsers(u.data); });
  }, [api]);

  const createDispute = async () => {
    await api.post('/disputes', form);
    const d = await api.get('/disputes');
    setDisputes(d.data);
    setShowCreate(false);
    setForm({ project_id: '', task_id: '', against_user: '', type: 'payment', description: '' });
  };

  const resolveDispute = async (id, status, resolution) => {
    await api.put(`/disputes/${id}`, { status, resolution: resolution || '' });
    const d = await api.get('/disputes');
    setDisputes(d.data);
  };

  const generateTimeline = async (id) => {
    setGeneratingTimeline(id);
    try {
      const res = await api.post(`/disputes/${id}/ai-timeline`);
      setTimeline({ id, content: res.data.timeline });
    } catch (e) { console.error(e); }
    setGeneratingTimeline(null);
  };

  const getName = (id) => users.find(u => u.id === id)?.full_name || id || 'N/A';

  return (
    <div className="space-y-6" data-testid="disputes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Dispute Resolution</h1>
          <p className="text-xs text-zinc-500 mt-1">Track and resolve disputes with AI-powered evidence timelines</p>
        </div>
        <button data-testid="create-dispute-btn" onClick={() => setShowCreate(true)} className="h-8 px-4 bg-red-600 hover:bg-red-500 text-white text-xs font-medium rounded-sm flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Raise Dispute
        </button>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-[28rem]">
            <h3 className="text-sm font-heading font-semibold text-white mb-4">Raise Dispute</h3>
            <div className="space-y-2.5">
              <div><label className="text-[10px] text-zinc-500 uppercase">Type</label>
                <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2">
                  {['payment', 'quality', 'deadline', 'scope'].map(t => <option key={t} value={t}>{t}</option>)}
                </select></div>
              <div><label className="text-[10px] text-zinc-500 uppercase">Project</label>
                <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2">
                  <option value="">Select</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></div>
              <div><label className="text-[10px] text-zinc-500 uppercase">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-3 py-2" /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={createDispute} className="flex-1 h-8 bg-red-600 text-white text-xs rounded-sm">Submit</button>
              <button onClick={() => setShowCreate(false)} className="flex-1 h-8 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Timeline View */}
      {timeline && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-3"><Bot className="w-4 h-4 text-blue-400" /><h3 className="text-xs font-semibold text-zinc-300 uppercase">AI-Generated Timeline</h3>
            <button onClick={() => setTimeline(null)} className="ml-auto text-[10px] text-zinc-500 hover:text-zinc-300">Close</button></div>
          <pre className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed bg-zinc-950 border border-zinc-800 rounded-sm p-4 max-h-60 overflow-y-auto">{timeline.content}</pre>
        </div>
      )}

      {/* Disputes List */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="divide-y divide-zinc-800/30">
          {disputes.map(d => (
            <div key={d.id} className="px-4 py-4" data-testid={`dispute-${d.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Scale className="w-3.5 h-3.5 text-red-400" />
                    <span className="text-[10px] font-medium text-zinc-400 uppercase">{d.type} dispute</span>
                    <span className={`status-${d.status === 'open' ? 'pending' : d.status === 'resolved' ? 'completed' : 'in_progress'} px-1.5 py-0.5 rounded-full text-[9px] font-medium`}>{d.status}</span>
                  </div>
                  <p className="text-xs text-zinc-200 mt-1">{d.description}</p>
                  <p className="text-[10px] text-zinc-600 mt-0.5">Raised by: {d.raised_by_email} | {d.created_at ? new Date(d.created_at).toLocaleDateString() : ''}</p>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => generateTimeline(d.id)} disabled={generatingTimeline === d.id} className="h-6 px-2 bg-blue-500/20 text-blue-400 text-[10px] rounded-sm hover:bg-blue-500/30 flex items-center gap-1">
                    <Bot className="w-3 h-3" /> {generatingTimeline === d.id ? 'Generating...' : 'AI Timeline'}
                  </button>
                  {d.status === 'open' && user?.role === 'master_admin' && (
                    <button onClick={() => resolveDispute(d.id, 'resolved', 'Resolved by admin')} className="h-6 px-2 bg-emerald-500/20 text-emerald-400 text-[10px] rounded-sm hover:bg-emerald-500/30">Resolve</button>
                  )}
                </div>
              </div>
              {d.resolution && <p className="text-[10px] text-emerald-400 mt-1 pl-5">Resolution: {d.resolution}</p>}
            </div>
          ))}
          {disputes.length === 0 && <p className="text-xs text-zinc-600 px-4 py-8 text-center">No disputes filed</p>}
        </div>
      </div>
    </div>
  );
}
